import {
  Box,
  Card,
  CardProps,
  Divider,
  Flex,
  HStack,
  Text,
} from '@chakra-ui/react';
import isNil from 'lodash/isNil';
import { FC, ReactNode } from 'react';
import { numberFormatter } from '~/common/utils/formatter';
import { getPropertySource } from '../../../utils/listing.utils';
import { getDaysOnMarketText } from '../../../utils/storefront.utils';
import { ListingImageInCard } from './ListingImageInCard';

export const MOBILE_CARD_MIN_HEIGHT = '150px';
export const MOBILE_IMAGE_MIN_WIDTH = '145px';

interface BaseListingCardProps extends CardProps {
  price?: number;
  streetNumberText?: string;
  streetName?: string;
  unit?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  bedrooms?: number;
  bathrooms?: string;
  area?: number;
  daysOnMarket?: number;
  photoSrc?: string;
  className?: string;
  button?: ReactNode;
  adminMenu?: ReactNode;
  otherTags?: ReactNode;
  topTags?: ReactNode;
  subscribersCount?: number;
  onCardClick?: () => void;
  fullWidth?: boolean;
  office?: string;
}

export const BaseListingCard: FC<BaseListingCardProps> = ({
  price,
  streetNumberText,
  streetName,
  unit,
  city,
  state,
  zipCode,
  bedrooms,
  bathrooms,
  area,
  daysOnMarket,
  photoSrc,
  className,
  button,
  adminMenu,
  otherTags,
  topTags,
  onCardClick,
  subscribersCount,
  fullWidth = false,
  office,
  ...otherProps
}) => {
  return (
    <Card
      className={className}
      cursor={onCardClick ? 'pointer' : 'default'}
      data-testid="base-listing-card"
      direction="column"
      flexShrink={0}
      minH="auto"
      overflow="hidden"
      userSelect="none"
      w={fullWidth ? 'full' : '260px'}
      onClick={onCardClick}
      {...otherProps}
    >
      <Box position="relative">
        {adminMenu}
        <ListingImageInCard
          addressFull={`${streetNumberText} ${streetName} ${unit}`}
          photo={getPropertySource(photoSrc)}
        />
        <Flex
          alignItems="flex-start"
          direction="column"
          flexGrow={0}
          gap={1}
          left="10px"
          position="absolute"
          textTransform="uppercase"
          top="10px"
        >
          {topTags}
        </Flex>
      </Box>
      <Flex
        direction="column"
        gap={1}
        lineHeight={1.25}
        p={3}
        pt={2.5}
        width="100%"
      >
        <HStack spacing={2.5}>
          <Text color="white" fontSize="lg" fontWeight="semibold">
            {price && price > 0
              ? numberFormatter(price, {
                  maxPrecision: 0,
                  type: 'currency',
                })
              : 'Price Unknown'}
          </Text>

          {otherTags}
        </HStack>
        {!isNil(subscribersCount) && (
          <Text color="whiteAlpha.600" fontSize="md">
            {subscribersCount} Subscribers
          </Text>
        )}
        <Divider />
        <Text color="whiteAlpha.600" fontSize="sm">
          {streetNumberText} {streetName} {unit}
          <br />
          {city}, {state} {zipCode}
        </Text>
        <Text color="whiteAlpha.600" fontSize="sm">
          {bedrooms} beds &bull;&nbsp;
          {bathrooms} baths &bull;&nbsp;
          {numberFormatter(area, {
            maxPrecision: 0,
          })}{' '}
          sq<sup>ft</sup> &bull;&nbsp;
          {getDaysOnMarketText(daysOnMarket, true)}
        </Text>
        {office && (
          <Text color="whiteAlpha.600" fontSize="xs">
            {office}
          </Text>
        )}
        {button}
      </Flex>
    </Card>
  );
};
